import { Test } from '@/payload-types'
import type { AnswerObject } from '@/types/answer.types'
import { Button, toast } from '@payloadcms/ui'
import React from 'react'

/**
 * Fetches the signed URL for a media file and forces download
 */
const handleFileDownload = async (
  fileId: number,
  fileName: string,
  forceDownload: boolean = true,
) => {
  try {
    const response = await fetch(`/api/media/${fileId}`)
    if (!response.ok) {
      throw new Error(`Failed to fetch media: ${response.status}`)
    }
    const mediaData = await response.json()

    if (!mediaData.url) {
      throw new Error('No URL found in media response')
    }

    if (forceDownload) {
      // Fetch the file as a blob and force download
      const fileResponse = await fetch(mediaData.url)
      if (!fileResponse.ok) {
        throw new Error(`Failed to fetch file: ${fileResponse.status}`)
      }
      const blob = await fileResponse.blob()
      const blobUrl = URL.createObjectURL(blob)

      // Create a temporary anchor element to force download
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up the blob URL
      URL.revokeObjectURL(blobUrl)
    } else {
      // Just open in new tab (for viewing PDFs, images, etc.)
      window.open(mediaData.url, '_blank')
    }
  } catch (error) {
    console.error('Error downloading file:', error)
    toast.error(`Failed to download ${fileName}`)
  }
}

/**
 * Check if file type is viewable in browser (PDFs, images)
 */
const isViewableInBrowser = (mimeType: string): boolean => {
  return mimeType === 'application/pdf' || mimeType.startsWith('image/')
}

/**
 * Download button component for file answers
 */
const FileDownloadButton: React.FC<{ fileId: number; fileName: string; mimeType?: string }> = ({
  fileId,
  fileName,
  mimeType,
}) => {
  const [loading, setLoading] = React.useState(false)
  const [action, setAction] = React.useState<'download' | 'view' | null>(null)

  const handleClick = async (forceDownload: boolean) => {
    setLoading(true)
    setAction(forceDownload ? 'download' : 'view')
    await handleFileDownload(fileId, fileName, forceDownload)
    setLoading(false)
    setAction(null)
  }

  const canView = mimeType && isViewableInBrowser(mimeType)

  return (
    <div className="flex items-center gap-2">
      <Button
        onClick={() => handleClick(true)}
        size="small"
        buttonStyle="secondary"
        margin={false}
        disabled={loading}
      >
        {loading && action === 'download' ? 'Downloading...' : 'Download'}
      </Button>
      {canView && (
        <Button
          onClick={() => handleClick(false)}
          size="small"
          buttonStyle="secondary"
          margin={false}
          disabled={loading}
        >
          {loading && action === 'view' ? 'Opening...' : 'View'}
        </Button>
      )}
    </div>
  )
}

export const renderStudentAnswer = (question: Test['questions'][number], answer: AnswerObject) => {
  if (!answer)
    return <span className="italic text-[var(--theme-elevation-400)]">No answer provided</span>

  switch (question.type) {
    case 'textarea':
      return (
        <div
          className="p-3 border rounded"
          style={{
            borderColor: 'var(--theme-elevation-200)',
            backgroundColor: 'var(--theme-elevation-100)',
          }}
        >
          <pre
            className="font-mono text-base whitespace-pre-wrap"
            style={{ color: 'var(--theme-text)' }}
          >
            {answer.value || 'No answer'}
          </pre>
        </div>
      )

    case 'radio': {
      // Find the selected option to display its text value
      const selectedOptionId = answer.value || answer.optionId
      const selectedOption = question.options?.find((opt) => opt.id === selectedOptionId)
      const displayText = selectedOption?.value || selectedOptionId || 'Unknown'

      return (
        <div
          className="p-3 border rounded"
          style={{
            borderColor: 'var(--theme-success-200)',
            backgroundColor: 'var(--theme-success-50)',
          }}
        >
          <strong style={{ color: 'var(--theme-success-800)' }}>Selected:</strong>{' '}
          <span style={{ color: 'var(--theme-success-700)' }}>{displayText}</span>
        </div>
      )
    }

    case 'checkbox': {
      const selectedValues = answer.values && Array.isArray(answer.values) ? answer.values : []

      return (
        <div
          className="p-3 border rounded"
          style={{
            borderColor: 'var(--theme-success-200)',
            backgroundColor: 'var(--theme-success-50)',
          }}
        >
          <strong style={{ color: 'var(--theme-success-800)' }}>Selected:</strong>
          {selectedValues.length > 0 ? (
            <ul className="mt-2 ml-4 list-disc">
              {selectedValues.map((val) => {
                // Find the option to display its text value
                const optionId = typeof val === 'object' ? val.value : val
                const selectedOption = question.options?.find((opt) => opt.id === optionId)
                const displayText = selectedOption?.value || optionId || 'Unknown'

                return (
                  <li key={optionId} style={{ color: 'var(--theme-success-700)' }}>
                    {displayText}
                  </li>
                )
              })}
            </ul>
          ) : (
            <span className="ml-2" style={{ color: 'var(--theme-elevation-400)' }}>
              No selections
            </span>
          )}
        </div>
      )
    }

    case 'file': {
      // Check if we have multiple files (new format) or single file (legacy format)
      const hasMultipleFiles = answer.files && answer.files.length > 0
      const hasSingleFile = answer.fileName && answer.fileId

      if (!hasMultipleFiles && !hasSingleFile) {
        return (
          <div
            className="p-3 border rounded"
            style={{
              borderColor: 'var(--theme-success-200)',
              backgroundColor: 'var(--theme-success-50)',
            }}
          >
            <span className="text-[var(--theme-elevation-400)]">No file uploaded</span>
          </div>
        )
      }

      return (
        <div
          className="p-3 border rounded"
          style={{
            borderColor: 'var(--theme-success-200)',
            backgroundColor: 'var(--theme-success-50)',
          }}
        >
          {hasMultipleFiles ? (
            // Multiple files
            <div>
              <strong style={{ color: 'var(--theme-success-800)' }}>
                Files ({answer.files!.length}):
              </strong>
              <div className="mt-2 space-y-3">
                {answer.files!.map((file, index) => (
                  <div
                    key={file.fileId}
                    className="pl-4 border-l-2"
                    style={{ borderColor: 'var(--theme-success-300)' }}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="text-xs font-semibold"
                        style={{ color: 'var(--theme-success-700)' }}
                      >
                        {index + 1}.
                      </span>
                      <span style={{ color: 'var(--theme-success-700)' }}>{file.fileName}</span>
                    </div>
                    <div className="mt-1 text-xs" style={{ color: 'var(--theme-success-600)' }}>
                      Size: {Math.round(file.fileSize / 1024)}KB • Type: {file.mimeType}
                    </div>
                    <div className="mt-2">
                      <FileDownloadButton
                        fileId={file.fileId}
                        fileName={file.fileName}
                        mimeType={file.mimeType}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Single file (legacy format)
            <div>
              <div className="flex items-center gap-2">
                <strong style={{ color: 'var(--theme-success-800)' }}>File:</strong>
                <span style={{ color: 'var(--theme-success-700)' }}>{answer.fileName}</span>
              </div>
              <div className="mt-1 text-xs" style={{ color: 'var(--theme-success-600)' }}>
                Size: {Math.round((answer.fileSize || 0) / 1024)}KB • Type: {answer.mimeType}
              </div>
              {answer.fileId && answer.fileName && (
                <div className="mt-4">
                  <FileDownloadButton
                    fileId={answer.fileId}
                    fileName={answer.fileName}
                    mimeType={answer.mimeType}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )
    }

    default:
      return <span className="text-[var(--theme-elevation-400)]">Unknown answer type</span>
  }
}

export const renderCorrectAnswers = (question: Test['questions'][number]) => {
  if (question.type !== 'radio' && question.type !== 'checkbox') return null

  const correctOptions = question.options?.filter((opt) => opt.isCorrect) || []
  if (correctOptions.length === 0)
    return <span className="text-[var(--theme-elevation-400)]">No correct answers marked</span>

  return (
    <div
      className="p-3 mt-3 border rounded"
      style={{
        borderColor: 'var(--theme-success-200)',
        backgroundColor: 'var(--theme-success-50)',
      }}
    >
      <strong style={{ color: 'var(--theme-success-800)' }}>Correct Answer(s):</strong>
      <ul className="mt-2 ml-4 list-disc">
        {correctOptions.map((option) => (
          <li key={option.id || option.value} style={{ color: 'var(--theme-success-700)' }}>
            {option.value}
          </li>
        ))}
      </ul>
    </div>
  )
}
